/**
 * "What's New" release notes, hand-maintained by engineers (WNB-2,
 * implementation-plan-whats-new.md). Newest release first —
 * `releaseNotes[0]` is the current version shown in the sidebar row and
 * used as the unread-dot comparison key (see `useWhatsNewUnread`).
 *
 * `date` is authored as `DD-MM-YYYY` (requirements-whats-new.md §7.1,
 * resolved 2026-10-16) and rendered as-is — no parsing/reformatting.
 *
 * Every item needs all four fields: `type` (new/fix/improved), `area`
 * (see its own doc comment below), `title`, `description` — the last
 * written to the plain-language content standard in
 * requirements-whats-new.md §9.1 (2-4 sentences, no internal/technical
 * terms: no API, database, config, UTC, HTTP, table/field names).
 */

export type ReleaseItemType = 'new' | 'fix' | 'improved';

export interface ReleaseNoteItem {
  readonly type: ReleaseItemType;
  /**
   * Which part of Qfly this touches, e.g. "Opportunities", "Reports".
   * Free text, not a closed union — requirements-whats-new.md §9.2
   * explains why (coupling to navConfig.ts's section names would force
   * every future nav rename to also touch old, frozen release-note
   * entries). Convention, not type-enforced: Title Case, 1-2 words;
   * prefer an existing navConfig.ts section name (Dashboard, Clients,
   * Marketing, Opportunities, Reports, Client Success, Admin, Partners)
   * when the item clearly belongs to one, otherwise a short noun phrase
   * a user would recognize. Never a sentence, never a verb phrase.
   */
  readonly area: string;
  readonly title: string;
  readonly description: string;
}

export interface ReleaseNote {
  readonly version: string;
  readonly date: string;
  readonly items: readonly ReleaseNoteItem[];
}

export const releaseNotes: readonly ReleaseNote[] = [
  {
    version: '1.1.0',
    date: '23-10-2026',
    items: [
      {
        type: 'new',
        area: 'Partners',
        title: 'A dedicated portal for partner deal registrations.',
        description:
          'Partners can now register deals themselves through their own portal, ' +
          "instead of emailing your team to enter them by hand. They can see their " +
          "campaigns and track each deal's status without having to ask you for an update.",
      },
      {
        type: 'new',
        area: 'Registrations',
        title: 'Review and approve partner deals from a single queue.',
        description:
          'Deals partners submit now land in a pending-approvals queue. From there you ' +
          'can approve or reject each one, and an approved deal can be linked to an ' +
          'existing opportunity or turned into a new one, with no extra data entry.',
      },
      {
        type: 'new',
        area: 'Admin',
        title: 'Manage partner portal access.',
        description:
          'Admins can now invite partner reps to the portal and control which of your ' +
          "own staff can access it, right from Qfly's admin screens.",
      },
    ],
  },
];
